import React from "react";

import {
  MOCK_DISCIPLINE_NOTES,
  MOCK_NURSE_NOTES,
  NURSE_AUTHOR,
  NURSE_DISCIPLINES,
  NURSE_TEMPLATES,
  dateTimeOfNow,
  type DisciplineNote,
  type NurseNote,
  type NurseTemplate,
} from "./mockPatientV4";
import {
  NURSE_AIE_FIELDS,
  NURSE_FIELDS,
  NURSE_FOCUS_NEW,
  NURSE_TEMPLATE_NEW_OPTION,
  applyNurseFocus,
  emptyNurseTemplateDraft,
  nurseTemplateDraftFromFocus,
  validateNurseTemplateDraft,
  type NurseTemplateDraft,
  errorTextOf,
  nurseNotePrintHtml,
  nurseNoteProgressParts,
  nurseNoteText,
  nurseFocusPreviewOf,
  nurseFocusTemplatesFromApi,
  nurseNotesInRange,
  normalizeExamV4Slice,
  beTodayText,
  nurseDateOfNow,
  removeNurseFocus,
  sortNurseNotesNewestFirst,
  type ExamV4Slice,
  type NurseFieldKey,
  type NurseFocusTemplate,
  type NurseNoteApi,
} from "./examModelV4";
import { printHtmlDocument } from "./printEmrV4";
import { CARD_NAMES } from "./examDispatchV4";
/* practice ของทีม: card import ชื่อ sequence + ACTIONS จากไฟล์ sequence (ดู CardOrdersV4.tsx:8-15) */
import {
  ACTIONS,
  SEQUENCE_NAME,
  type ExamRunParamsLoose,
  type ExamRunSequenceProp,
} from "../common/sequence/ExamV4";
import {
  ACTIONS as CONSULT_ACTIONS,
  CARD_NAME as CONSULT_CARD_NAME,
  SEQUENCE_NAME as CONSULT_SEQUENCE,
} from "../common/sequence/MultidisciplinaryV4";
import { AlertV4, ButtonV4, FieldV4, ModalV4, SectionSpinner, SelectV4 } from "../common/ui";
import { anyBusy } from "../common/ui/SectionSpinnerV4";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import {
  CONSULT_DISCIPLINES,
  consultAuthorText,
  consultDateOfNow,
  consultDateRangeText,
  consultDisciplineFilterOptions,
  consultLogBadgeClassOf,
  consultLogUserParts,
  consultNoteEditable,
  consultNoteText,
  consultPrintHtml,
  normalizeConsultV4Slice,
  sortConsultNotesNewestFirst,
  type ConsultDraft,
  type ConsultLog,
  type ConsultNote,
  type ConsultV4Slice,
} from "./consultModelV4";
import type { PatientMenuKey } from "./PatientPanelV4";

/**
 * บันทึกการพยาบาล (6b ของหน้าตรวจรักษา) ตามแบบ PatientNursing.dc.html
 * Template ของ design มี 3 แบบ แก้ชื่อ/ช่องบันทึกได้ผ่านปุ่ม ⚙ ตั้งค่า template
 * ข้อมูลยังเป็นตัวอย่าง ยังไม่ต่อ API
 */

/** template ที่ UI ใช้ (mock label = key, live key = คอลัมน์ของ API) */
type NursingTemplate = { name: string; fields: { key: string; label: string }[] };

type NoteView = {
  id?: number;
  text: string;
  template: string;
  by: string;
  at: string;
  /** เวรที่บันทึก (`working_shift`) — API คืนค่ากลับมา V3 แสดงเฉพาะ IPD */
  shift?: string;
  /** order ที่บันทึกผูกด้วย (`doctor_order_id`) */
  orderId?: string | null;
  /** ค่าของแต่ละช่อง (focus/diagnosis/plan/goal) ใช้ตอนเติมกลับเมื่อแก้ */
  values: Record<string, string>;
};

export type CardNursingV4Props = {
  onSelectMenu: (menu: Extract<PatientMenuKey, "doctor" | "gallery" | "nursing">) => void;
  /** slice ของ ExamV4 sequence (live mode) */
  exam?: Partial<ExamV4Slice> | null;
  /** slice ของ MultidisciplinaryV4 sequence (live mode) */
  consult?: Partial<ConsultV4Slice> | null;
  /** user ปัจจุบัน — backend ล็อกแก้/ลบเฉพาะบันทึกของตัวเอง */
  currentUserId?: number;
  /** username ของผู้ใช้ที่ login (ใช้เติมช่อง Usernameของผู้บันทึก — backend ต้องการ username/password) */
  currentUsername?: string;
  /** คำสั่งแพทย์ของ encounter (ใช้ผูกบันทึกกับ `doctor_order_id` แบบ V3 PTMI.ts:808-810) */
  doctorOrders?: { id: number; label: string }[];
  /** ตาม practice ของทีม: card เรียก sequence ของ MainHIS (ดู CardOrdersV4.tsx:29-31) */
  runSequence?: ExamRunSequenceProp;
  /** loading ต่อ action: key "<card>_<ACTION>" (BLC_STATUS ของ CommonInterface) */
  buttonLoadCheck?: Record<string, any>;
  /** error ของ sequenceนี้: key = ชื่อ card (SetErrorMessage ของ CommonInterface) */
  errorMessage?: Record<string, any>;
  /** ปิดแถบ error: setProp("errorMessage.<card>", null) แบบเดียวกับ CardOrdersV4 */
  setProp?: (key: string, value: any) => void;
};

const emptyFieldValues = (): Record<string, string> => ({});

const toNursingTemplate = (template: NurseTemplate): NursingTemplate => ({
  name: template.name,
  fields: template.fields.map((field) => ({ key: field, label: field })),
});

const noteViewFromApi = (note: NurseNoteApi): NoteView => ({
  id: note.id,
  text: nurseNoteText(note),
  template: "",
  by: note.nurse !== "" ? note.nurse : "พยาบาล",
  at: note.date,
  shift: note.shift,
  orderId: note.doctorOrderId,
  values: {
    focus: note.focus,
    diagnosis: note.diagnosis,
    plan: note.plan,
    goal: note.goal,
  },
});

export const CardNursingV4: React.FC<CardNursingV4Props> = ({
  onSelectMenu,
  exam,
  consult,
  currentUserId,
  currentUsername,
  doctorOrders,
  runSequence,
  buttonLoadCheck,
  errorMessage,
  setProp,
}) => {
  /* ---------- Multidisciplinary (คนละ sequence/card key กับ Nurse Note) ---------- */
  const consultSlice =
    consult === undefined || consult === null ? null : normalizeConsultV4Slice(consult);
  const consultIds = consultSlice?.ids ?? {};
  const busyConsult = (action: string): boolean =>
    buttonLoadCheck?.[`${CONSULT_CARD_NAME}_${action}`] === "LOADING";
  const consultError = errorTextOf(errorMessage?.[CONSULT_CARD_NAME]);

  const examSlice = exam === undefined || exam === null ? null : normalizeExamV4Slice(exam);
  const sliceIds = examSlice?.ids ?? {};
  /* liveเมื่อมี encounter id จาก sliceใดsliceหนึ่ง (nurse note ใช้ ExamV4, บันทึกทีม ใช้ MultidisciplinaryV4) */
  const live =
    (!!examSlice && !!examSlice.ids.encounterId) ||
    (!!consultSlice && !!consultSlice.ids.encounterId);

  const cardName = CARD_NAMES.nursing;
  const dispatch = (params: ExamRunParamsLoose): void => {
    runSequence?.(params);
  };
  const busy = (action: string): boolean =>
    buttonLoadCheck?.[`${cardName}_${action}`] === "LOADING";
  const loadError = (): string | null => errorTextOf(errorMessage?.[cardName]);
  /* icon หมุนท้ายชื่อหัวข้อตาม buttonLoadCheck จริงของแต่ละ sequence (เหมือนหน้าคัดกรอง) */
  const nurseSectionLoading = anyBusy(busy, [
    ACTIONS.LOAD_NURSE,
    ACTIONS.SAVE_NURSE_NOTE,
    ACTIONS.UPDATE_NURSE_NOTE,
    ACTIONS.DELETE_NURSE_NOTE,
  ]);
  const consultSectionLoading = anyBusy(busyConsult, [
    CONSULT_ACTIONS.LOAD,
    CONSULT_ACTIONS.CREATE,
    CONSULT_ACTIONS.UPDATE,
    CONSULT_ACTIONS.DELETE,
    CONSULT_ACTIONS.LOAD_LOGS,
  ]);

  const [templates, setTemplates] = React.useState<NursingTemplate[]>(
    NURSE_TEMPLATES.map(toNursingTemplate),
  );
  const [templateName, setTemplateName] = React.useState(NURSE_TEMPLATES[0].name);
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>(emptyFieldValues);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsName, setSettingsName] = React.useState("");
  const [settingsFields, setSettingsFields] = React.useState("");
  /* 86637: ร่าง Focus ที่กำลังแก้ไขในกล่องตั้งค่า template (live mode เท่านั้น) */
  const [templateDraft, setTemplateDraft] = React.useState<NurseTemplateDraft>(emptyNurseTemplateDraft());
  const [templateHint, setTemplateHint] = React.useState("");

  /* 86636: Focus ที่เลือก (id ของ NurseNoteTemplate) + 86638: ค้นหาช่วงวันที่ของตาราง */
  const [focusId, setFocusId] = React.useState<number | null>(null);
  const [noteSearchFrom, setNoteSearchFrom] = React.useState("");
  const [noteSearchTo, setNoteSearchTo] = React.useState("");
  const [noteSearchApplied, setNoteSearchApplied] = React.useState<{ from: string; to: string } | null>(null);
  /** id ของบันทึกที่เลือกไว้สำหรับ Print (86641) */
  const [checkedNoteIds, setCheckedNoteIds] = React.useState<number[]>([]);
  /** การลบยืนยันในตาราง (86636 ของ issue 86640: no modal) */
  const [noteDeletingId, setNoteDeletingId] = React.useState<number | null>(null);
  const [noteDeletePassword, setNoteDeletePassword] = React.useState("");
  const [notePrintMessage, setNotePrintMessage] = React.useState<string | null>(null);
  /** ข้อความยืนยันที่ผู้ใช้ปิดแล้ว (กันแสดงซ้ำหลัง reload slice) */
  const [dismissedNurseMessage, setDismissedNurseMessage] = React.useState("");
  const nurseFormRef = React.useRef<HTMLDivElement | null>(null);

  const [notes, setNotes] = React.useState<NoteView[]>(
    MOCK_NURSE_NOTES.map((note) => ({
      text: note.text,
      template: note.template,
      by: note.by,
      at: note.at,
      values: {},
    })),
  );
  const [editingNoteId, setEditingNoteId] = React.useState<number | null>(null);
  /* เวรที่บันทึก (working_shift) — ช่องเดียวกับ V3 (SubNurseNoteItem.tsx:176-191) */
  const [noteShift, setNoteShift] = React.useState("");
  /** คำสั่งแพทย์ที่บันทึกนี้ผูกด้วย (doctor_order_id) */
  const [noteDoctorOrderId, setNoteDoctorOrderId] = React.useState<number | null>(null);
  /* ผู้บันทึกของบันทึกการพยาบาล: backend ต้องการ username/password หรือ user_token ที่ valid */
  const [noteCredentials, setNoteCredentials] = React.useState({
    username: currentUsername ?? "",
    password: "",
  });
  const [deletingNote, setDeletingNote] = React.useState<NoteView | null>(null);
  const [noteDeleteCredentials, setNoteDeleteCredentials] = React.useState({
    username: currentUsername ?? "",
    password: "",
  });
  const [discipline, setDiscipline] = React.useState(NURSE_DISCIPLINES[1]);
  const [disciplineText, setDisciplineText] = React.useState("");
  const [disciplineNotes, setDisciplineNotes] =
    React.useState<DisciplineNote[]>(MOCK_DISCIPLINE_NOTES);

  /** draft ของบันทึกทีม (live mode) */
  const [consultDraft, setConsultDraft] = React.useState<ConsultDraft>({
    date: "",
    endDate: "",
    problem: "",
    plan: "",
    goal: "",
    username: currentUsername ?? "",
    password: "",
  });
  const [deleteCredentials, setDeleteCredentials] = React.useState({
    username: currentUsername ?? "",
    password: "",
  });
  const [consultUseDates, setConsultUseDates] = React.useState(false);
  const [consultFilterRole, setConsultFilterRole] = React.useState<string | number>("");
  const [consultDateFrom, setConsultDateFrom] = React.useState("");
  const [consultDateTo, setConsultDateTo] = React.useState("");
  /* 86643: ย่อ/ขยายทั้งการ์ด (form + filter + table) */
  const [consultOpen, setConsultOpen] = React.useState(true);
  /** id ที่เลือกสำหรับ Print (86647) */
  const [checkedConsultIds, setCheckedConsultIds] = React.useState<number[]>([]);
  const [consultPrintMessage, setConsultPrintMessage] = React.useState<string | null>(null);
  const [dismissedConsultMessage, setDismissedConsultMessage] = React.useState("");
  const consultFormRef = React.useRef<HTMLDivElement | null>(null);

  /** id ของแถวที่กำลังแก้ / กำลังยืนยันลบ / ที่แสดง action log */
  const [editingConsultId, setEditingConsultId] = React.useState<number | null>(null);
  const [deletingConsultId, setDeletingConsultId] = React.useState<number | null>(null);
  const [logsConsultId, setLogsConsultId] = React.useState<number | null>(null);

  const nurseKey = examSlice
    ? `${sliceIds.encounterId ?? ""}|${examSlice.savedAt ?? ""}|${examSlice.nurseNotes.map((note) => note.id).join(",")}|${examSlice.nurseTemplates.map((item) => item.id).join(",")}`
    : "";

  React.useEffect(() => {
    if (!examSlice || !examSlice.ids.encounterId) return;

    if (examSlice.nurseTemplates.length > 0) {
      const apiTemplates = examSlice.nurseTemplates.map((item) => ({
        name: item.name,
        fields: item.fields.map((field) => ({ key: field.key, label: field.label })),
      }));
      setTemplates(apiTemplates);
      setTemplateName((current) =>
        apiTemplates.some((item) => item.name === current) ? current : apiTemplates[0].name,
      );
    }

    setNotes(
      examSlice.nurseNotes
        .filter((note) => note.active)
        .map(noteViewFromApi)
        .filter((note) => note.text !== ""),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nurseKey]);

  React.useEffect(() => {
    if (!live) return undefined;
    /* 86638 ข้อ 4: default ช่องค้นหา = วันปัจจุบัน */
    setNoteSearchFrom((current) => (current === "" ? beTodayText() : current));
    setNoteSearchTo((current) => (current === "" ? beTodayText() : current));
    /* V3 editor ลงStart Dateด้วยวันปัจจุบัน (MultiDisciplinaryEditor.tsx:46-53) */
    setConsultDraft((current) =>
      current.date === "" ? { ...current, date: consultDateOfNow() } : current,
    );
    return undefined;
  }, [live]);

  const dispatchReload = () =>
    dispatch({ sequence: SEQUENCE_NAME, action: ACTIONS.LOAD_NURSE, card: cardName, ...sliceIds });

  const template = templates.find((item) => item.name === templateName) ?? templates[0];

  /* ---------- Focus (issue 86636) ----------
     dropdown ใช้แถวของ NurseNoteTemplate (nurseFocusTemplatesFromApi) และเติมข้อความ
     ลงช่อง Assessment (A) / Intervention (I) โดยตรง เพราะ backend เก็บ A-I-E
     ในคอลัมน์ diagnosis / plan / goal ของ NurseNoteItem (V3 CardNurseNote.tsx:177-189) */
  const focusTemplates = examSlice?.nurseFocusTemplates ?? [];
  const focusOptions = focusTemplates.map((item) => ({
    value: String(item.id),
    text: item.name,
    preview: nurseFocusPreviewOf(item),
  }));
  const selectedFocus: NurseFocusTemplate | null =
    focusTemplates.find((item) => item.id === focusId) ?? null;

  const chooseFocus = (nextId: number): void => {
    const next = focusTemplates.find((item) => item.id === nextId) ?? null;
    setFocusId(nextId);
    if (next === null) return;
    setFieldValues((current) => {
      /* เปลี่ยน Focus: ถอดข้อความของ Focus เดิมออกก่อน (เฉพาะส่วนที่ผู้ใช้ไม่ได้แก้) แล้วเติมของใหม่ */
      const cleaned =
        selectedFocus === null ? current : removeNurseFocus(current, selectedFocus).values;
      return applyNurseFocus(cleaned, next);
    });
  };

  const clearFocus = (): void => {
    setFocusId(null);
    if (selectedFocus === null) return;
    setFieldValues((current) => removeNurseFocus(current, selectedFocus).values);
  };

  /* 86635: ช่องบันทึกคงที่ 3 ช่อง (A / I / E) ไม่ขึ้นกับ template */
  const liveFields = NURSE_AIE_FIELDS.map((field) => ({ key: field.key, label: field.label }));
  const activeFields = live ? liveFields : template.fields;
  const anyFieldFilled = activeFields.some(
    (field) => (fieldValues[field.key] ?? "").trim() !== "",
  );
  const addNoteDisabled = !anyFieldFilled;

  /* ---------- ตาราง Nurse Note (issue 86638 / 86641) ---------- */
  const nurseRows = (): NurseNoteApi[] => {
    const active = (examSlice?.nurseNotes ?? []).filter((note) => note.active);
    const searched =
      noteSearchApplied === null
        ? active
        : nurseNotesInRange(active, noteSearchApplied.from, noteSearchApplied.to);
    return sortNurseNotesNewestFirst(searched);
  };

  const applyNoteSearch = (): void => {
    setNoteSearchApplied({ from: noteSearchFrom, to: noteSearchTo });
    setCheckedNoteIds([]);
  };

  const toggleNoteChecked = (noteId: number): void => {
    setCheckedNoteIds((current) =>
      current.includes(noteId) ? current.filter((id) => id !== noteId) : current.concat(noteId),
    );
  };

  const toggleAllNotesChecked = (): void => {
    const ids = nurseRows().map((note) => note.id);
    setCheckedNoteIds((current) => (current.length === ids.length ? [] : ids.slice()));
  };

  /** แถวที่แสดงในตาราง (กรองช่วงวันที่ + เรียงใหม่สุดก่อน) ตาม issue 86638 ข้อ 3/4 */
  const liveNoteRows = live ? nurseRows() : [];

  const printSelectedNotes = (): void => {
    const picked = nurseRows().filter((note) => checkedNoteIds.includes(note.id));
    if (picked.length === 0) return;
    if (printHtmlDocument(nurseNotePrintHtml(picked, { encounterNumber: String(sliceIds.encounterId ?? "") }))) {
      setNotePrintMessage(`ส่งพิมพ์บันทึกการพยาบาลที่เลือก ${picked.length} รายการ`);
    }
  };
  const addDisciplineDisabled = disciplineText.trim() === "";

  const openSettings = (): void => {
    setSettingsName(template.name);
    setSettingsFields(template.fields.map((field) => field.label).join("\n"));
    if (live && !settingsOpen) {
      /* เริ่มจาก Focus ที่เลือกไว้ในฟอร์มบันทึก ถ้ายังไม่เลือก = โหมดสร้างใหม่ */
      setTemplateDraft(
        selectedFocus === null ? emptyNurseTemplateDraft() : nurseTemplateDraftFromFocus(selectedFocus),
      );
      setTemplateHint("");
    }
    setSettingsOpen((current) => !current);
  };

  /** 86637 ข้อ 2: "+ สร้าง template ใหม่" -> dropdown แสดง "— Focus ใหม่ (ยังไม่บันทึก) —" */
  const startNewFocusTemplate = (): void => {
    setTemplateDraft(emptyNurseTemplateDraft());
    setTemplateHint("");
  };

  const pickFocusToEdit = (value: string): void => {
    if (value === NURSE_FOCUS_NEW) {
      setTemplateDraft(emptyNurseTemplateDraft());
      return;
    }
    const next = focusTemplates.find((item) => String(item.id) === value);
    setTemplateDraft(next === undefined ? emptyNurseTemplateDraft() : nurseTemplateDraftFromFocus(next));
  };

  const setDraftText = (key: NurseFieldKey, value: string): void => {
    setTemplateDraft((current) => ({ ...current, texts: { ...current.texts, [key]: value } }));
  };

  const saveFocusTemplate = (): void => {
    const invalid = validateNurseTemplateDraft(templateDraft);
    setTemplateHint(invalid ?? "");
    if (invalid !== null) return;
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.SAVE_NURSE_TEMPLATE,
      card: cardName,
      ...sliceIds,
      draft: templateDraft,
    });
  };

  const deleteFocusTemplate = (): void => {
    if (templateDraft.itemId === null) return;
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.DELETE_NURSE_TEMPLATE,
      card: cardName,
      ...sliceIds,
      itemId: templateDraft.itemId,
      name: templateDraft.name,
    });
  };

  const saveTemplate = (): void => {
    const name = settingsName.trim();
    const lines = settingsFields
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
    if (name === "" || lines.length === 0) return;

    const fields = lines.map((line: string) => ({ key: line, label: line }));
    const exists = templates.some((item) => item.name === name);
    const updated = exists
      ? templates.map((item) => (item.name === name ? { name, fields } : item))
      : templates.concat({ name, fields });

    setTemplates(updated);
    setTemplateName(name);
    setFieldValues(emptyFieldValues());
    setSettingsOpen(false);
  };

  const deleteTemplate = (): void => {
    if (templates.length <= 1) return;
    const updated = templates.filter((item) => item.name !== template.name);
    setTemplates(updated);
    setTemplateName(updated[0].name);
    setFieldValues(emptyFieldValues());
    setSettingsOpen(false);
  };

  const addNote = (): void => {
    if (addNoteDisabled) return;
    const multiple = template.fields.length > 1;
    const filled = template.fields.filter((field) => (fieldValues[field.key] ?? "").trim() !== "");
    const text = filled
      .map(
        (field) =>
          (multiple ? `${field.label.replace(/ \(.\)$/, "")}: ` : "") +
          (fieldValues[field.key] ?? "").trim(),
      )
      .join(" / ");

    if (live) {
      /* 86635/86638: A / I / E ลงคอลัมน์ diagnosis / plan / goal ของ NurseNoteItem
         (V3 CardNurseNote.tsx:177-189) และชื่อ Focus ลงคอลัมน์ focus */
      const values: Partial<Record<NurseFieldKey, string>> = {};
      if (selectedFocus !== null && selectedFocus.name.trim() !== "")
        values.focus = selectedFocus.name.trim();
      for (const field of NURSE_AIE_FIELDS) {
        const text = (fieldValues[field.key] ?? "").trim();
        if (text !== "") values[field.key] = text;
      }
      if (editingNoteId === null) {
        /* optimistic เฉพาะการสร้างใหม่; แก้ไขให้รอค่าจริงจาก API (patch คืน field ไม่ครบชุด) */
        setNotes((current) =>
          current.concat({
            text,
            template: multiple ? template.name : "",
            by: "กำลังบันทึก...",
            at: dateTimeOfNow(),
            values,
          }),
        );
      }

      dispatch({
        sequence: SEQUENCE_NAME,
        action: editingNoteId === null ? ACTIONS.SAVE_NURSE_NOTE : ACTIONS.UPDATE_NURSE_NOTE,
        card: cardName,
        ...sliceIds,
        ...(editingNoteId === null ? {} : { noteId: editingNoteId }),
        values,
        date: nurseDateOfNow(),
        username: noteCredentials.username,
        password: noteCredentials.password,
        workingShift: noteShift.trim() === "" ? undefined : noteShift.trim(),
        doctorOrderId: noteDoctorOrderId ?? undefined,
      });
      setEditingNoteId(null);
      setNoteShift("");
      setNoteDoctorOrderId(null);
      /* 86635 ข้อ 5: ล้างช่องบันทึกและ Focus ที่เลือกแล้ว (เก็บ Focus ไว้ใช้ซ้ำได้) */
      setFieldValues(emptyFieldValues());
      setFocusId(null);
      return;
    }

    setNotes((current) =>
      current.concat({
        text,
        template: multiple ? template.name : "",
        by: NURSE_AUTHOR,
        at: dateTimeOfNow(),
        values: {},
      }),
    );
    setFieldValues(emptyFieldValues());
  };

  /**
   * แก้ไข: เติมกลับช่องของ template จากค่าใน note (NurseNoteItemSerializer มี focus/diagnosis/plan/goal)
   * ถ้า template ที่เลือกอยู่ไม่มีช่องที่มีค่า ให้สลับไป template ที่มีช่องนั้นแทน
   */
  const openNoteEdit = (note: NoteView): void => {
    if (note.id === undefined) return;

    const candidates = [template]
      .concat(templates)
      .find((item) => item.fields.some((field) => (note.values[field.key] ?? "") !== ""));
    const target = candidates ?? template;

    const values: Record<string, string> = {};
    for (const field of target.fields) values[field.key] = note.values[field.key] ?? "";

    setEditingNoteId(note.id);
    if (target.name !== template.name) setTemplateName(target.name);
    /* 86639 ข้อ 1: เติม Focus กลับมาด้วย (จับชื่อ Focus กับรายการ template ที่อ่านได้) */
    const focusRow = (examSlice?.nurseFocusTemplates ?? []).find(
      (item) => item.name === (note.values.focus ?? ""),
    );
    setFocusId(focusRow?.id ?? null);
    /* 86639 ข้อ 2: scroll กลับไปฟอร์มด้านบน (ผู้ใช้เห็นช่องที่แก้) */
    if (nurseFormRef.current !== null && typeof nurseFormRef.current.scrollIntoView === "function") {
      nurseFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setNoteShift(note.shift ?? "");
    setNoteDoctorOrderId(note.orderId === null || note.orderId === undefined ? null : Number(note.orderId));
    setFieldValues(values);
  };

  const cancelNoteEdit = (): void => {
    setEditingNoteId(null);
    setNoteShift("");
    setNoteDoctorOrderId(null);
    setFocusId(null);
    setFieldValues(emptyFieldValues());
  };

  /** เปิด modal ยืนยันลบ (V3 ใช้ ModAuthen: ลบได้เฉพาะเมื่อยืนยันตัวผู้ลบ) */
  const deleteNote = (note: NoteView): void => {
    if (!live || note.id === undefined) return;
    setDeletingNote(note);
    setNoteDeleteCredentials({ username: currentUsername ?? "", password: "" });
  };

  /** 86640: แถบยืนยันลบอยู่ใต้แถวเดิม (no modal) — กรอกได้เฉพาะรหัสผ่านของผู้บันทึก */
  const startNoteDelete = (note: NoteView): void => {
    if (note.id === undefined) return;
    setDeletingNote(note);
    setNoteDeleteCredentials({ username: currentUsername ?? "", password: "" });
  };

  const cancelNoteDelete = (): void => {
    setDeletingNote(null);
    setNoteDeleteCredentials({ username: currentUsername ?? "", password: "" });
  };

  const confirmDeleteNote = (): void => {
    if (deletingNote === null || deletingNote.id === undefined) return;
    dispatch({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.DELETE_NURSE_NOTE,
      card: cardName,
      ...sliceIds,
      noteId: deletingNote.id,
      username: noteDeleteCredentials.username,
      password: noteDeleteCredentials.password,
    });
    setDeletingNote(null);
  };

  const setNoteCredential = (
    field: "username" | "password",
    value: string,
    target: "save" | "delete",
  ): void => {
    if (target === "save") {
      setNoteCredentials((current) => ({ ...current, [field]: value }));
    } else {
      setNoteDeleteCredentials((current) => ({ ...current, [field]: value }));
    }
  };

  /* ---------- Multidisciplinary: dispatch / filter / write ---------- */

  const dispatchConsult = (action: string, extra: Record<string, unknown> = {}): void => {
    dispatch({
      sequence: CONSULT_SEQUENCE,
      action,
      card: CONSULT_CARD_NAME,
      ...consultIds,
      ...extra,
    });
  };

  const reloadConsult = (
    next: {
      role?: string | number;
      from?: string;
      to?: string;
      useDates?: boolean;
    } = {},
  ): void => {
    const useDates = next.useDates === undefined ? consultUseDates : next.useDates;
    const from = next.from === undefined ? consultDateFrom : next.from;
    const to = next.to === undefined ? consultDateTo : next.to;

    dispatchConsult(CONSULT_ACTIONS.LOAD, {
      filter: {
        practitionerRole: next.role === undefined ? consultFilterRole : next.role,
        startDate: useDates ? from : "",
        endDate: useDates ? to : "",
      },
    });
  };

  const consultRoleOptions = consultSlice?.roleOptions ?? [];
  const consultRoleOptionsUsable = consultRoleOptions.filter((option) => `${option.value}` !== "");

  const consultNotesOf = (notes: ConsultNote[]): ConsultNote[] =>
    notes.filter((note) => note.active);

  const allConsultNotes = consultNotesOf(consultSlice?.notes ?? []);

  /**
   * dropdown "วิชาชีพ"
   * - ปกติกรองด้วย practitioner_role (id) ตาม V3 (choices API users.Role.type)
   * - ถ้า choices API ล้ม/แผนกไม่มี role: ใช้ label ที่พบในแถวจริงเป็นตัวเลือก แล้วกรอง client side
   *   (ถ้าใช้ consultDisciplineFilterOptions กับ roleOptions เปล่า จะได้ dropdown มีแค่ "ทั้งหมด"
   *   แล้ว filter จะไม่ตรงกับแถวไหนเลย — เคยทำให้รายการหายทั้งหน้า)
   */
  const consultRoleLabels: string[] = ["ทั้งหมด"].concat(
    consultRoleOptionsUsable.length > 0
      ? consultDisciplineFilterOptions(consultRoleOptions)
      : Array.from(new Set(allConsultNotes.map((note) => note.discipline))),
  );

  const consultRoleLabel =
    consultFilterRole === "" || consultFilterRole === 0
      ? "ทั้งหมด"
      : (consultRoleOptions.find((option) => `${option.value}` === `${consultFilterRole}`)?.label ??
        `${consultFilterRole}`);

  const visibleConsultNotes =
    consultFilterRole === "" || consultFilterRole === 0
      ? allConsultNotes
      : consultRoleOptionsUsable.length > 0
        ? allConsultNotes.filter((note) =>
            consultRoleOptions.some(
              (option) =>
                `${option.value}` === `${consultFilterRole}` && option.label === note.discipline,
            ),
          )
        : allConsultNotes.filter((note) => note.discipline === consultRoleLabel);

  const editingConsult = allConsultNotes.find((note) => note.id === editingConsultId) ?? null;
  const deletingConsult = allConsultNotes.find((note) => note.id === deletingConsultId) ?? null;
  const consultLogs: ConsultLog[] =
    logsConsultId === null ? [] : (consultSlice?.logsByItem[String(logsConsultId)] ?? []);
  const editingConsultLogs =
    logsConsultId === null
      ? null
      : allConsultNotes.find((note) => note.id === logsConsultId) ?? null;

  const consultSaving = busyConsult(CONSULT_ACTIONS.CREATE) || busyConsult(CONSULT_ACTIONS.UPDATE);

  const setConsultDraftField = (field: keyof ConsultDraft, value: string): void => {
    const nextValue = value;
    setConsultDraft((current) => ({ ...current, [field]: nextValue }));
  };

  const handleConsultDiscipline = (label: string): void => {
    const option = consultRoleOptions.find((item) => item.label === label);
    const role = label === "ทั้งหมด" ? "" : option === undefined ? label : option.value;
    setConsultFilterRole(role);
    reloadConsult({ role });
  };

  const handleConsultToggleDates = (): void => {
    const next = !consultUseDates;
    setConsultUseDates(next);
    reloadConsult({ useDates: next });
  };

  const handleConsultDateFrom = (value: string): void => {
    setConsultDateFrom(value);
    if (consultUseDates) reloadConsult({ from: value });
  };

  const handleConsultDateTo = (value: string): void => {
    setConsultDateTo(value);
    if (consultUseDates) reloadConsult({ to: value });
  };

  /* 86643 ข้อ 4: รายการใหม่สุดอยู่บน (ใช้ created จาก API) */
  const consultRows = (): ConsultNote[] => sortConsultNotesNewestFirst(visibleConsultNotes);

  const toggleConsultChecked = (noteId: number): void => {
    setCheckedConsultIds((current) =>
      current.includes(noteId) ? current.filter((id) => id !== noteId) : current.concat(noteId),
    );
  };

  /* 86647 ข้อ 1: checkbox หัวตารางเลือกทั้งหมด / ไม่มีเลย */
  const toggleAllConsultChecked = (): void => {
    const ids = consultRows().map((note) => note.id);
    setCheckedConsultIds((current) => (current.length === ids.length ? [] : ids.slice()));
  };

  const printSelectedConsults = (): void => {
    const picked = consultRows().filter((note) => checkedConsultIds.includes(note.id));
    if (picked.length === 0) return;
    if (printHtmlDocument(consultPrintHtml(picked, { encounterNumber: String(consultIds.encounterId ?? "") }))) {
      setConsultPrintMessage(`ส่งพิมพ์ Multidisciplinary ที่เลือก ${picked.length} รายการ`);
    }
  };

  const openConsultEdit = (note: ConsultNote): void => {
    setEditingConsultId(note.id);
    /* 86644 ข้อ 2/3: เติมข้อมูลกลับ + scroll ไปฟอร์ม + ไม่คืนค่า Username/Password */
    if (consultFormRef.current !== null && typeof consultFormRef.current.scrollIntoView === "function") {
      consultFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setConsultDraft({
      date: note.date,
      endDate: note.endDate,
      problem: note.problem,
      plan: note.plan,
      goal: note.goal,
      username: "",
      password: "",
    });
    setLogsConsultId(null);
  };

  const cancelConsultEdit = (): void => {
    setEditingConsultId(null);
    setConsultDraft(emptyConsultDraft(consultDateOfNow(), currentUsername));
  };

  /**
   * backend ต้องการ username/password ของผู้บันทึกเพื่อยืนยันเรื่อง
   * (HIS V3: MultiDisciplinaryEditor.tsx:97-98 + CardMultiDisciplinaryView.tsx:625-632)
   */
  const emptyConsultDraft = (date: string, username?: string): ConsultDraft => ({
    date,
    endDate: "",
    problem: "",
    plan: "",
    goal: "",
    username: username ?? "",
    password: "",
  });

  const saveConsult = (): void => {
    const draft =
      consultDraft.date.trim() === ""
        ? { ...consultDraft, date: consultDateOfNow() }
        : consultDraft;

    if (editingConsult === null) {
      dispatchConsult(CONSULT_ACTIONS.CREATE, { draft });
    } else {
      dispatchConsult(CONSULT_ACTIONS.UPDATE, { noteId: editingConsult.id, draft });
      setEditingConsultId(null);
    }

    setConsultDraft(emptyConsultDraft(consultDateOfNow(), currentUsername));
  };

  const confirmDeleteConsult = (): void => {
    if (deletingConsult === null) return;
    /* V3 ห้ามลบถ้าไม่ยืนยันตัวตน -> ส่ง credentials ไปกับ action */
    dispatchConsult(CONSULT_ACTIONS.DELETE, {
      noteId: deletingConsult.id,
      username: deleteCredentials.username,
      password: deleteCredentials.password,
    });
    setDeletingConsultId(null);
    setDeleteCredentials({ username: currentUsername ?? "", password: "" });
  };

  const openConsultLogs = (note: ConsultNote): void => {
    setLogsConsultId(note.id);
    dispatchConsult(CONSULT_ACTIONS.LOAD_LOGS, { itemId: note.id });
  };

  const addDisciplineNote = (): void => {
    if (addDisciplineDisabled) return;
    setDisciplineNotes((current) =>
      current.concat({
        discipline,
        text: disciplineText.trim(),
        by: `${discipline}ตัวอย่าง`,
        at: dateTimeOfNow(),
      }),
    );
    setDisciplineText("");
  };

  return (
    <>
      <div className="hv4-grp-tabs">
        <button type="button" className="hv4-grp-tab" onClick={() => onSelectMenu("doctor")}>
          ประวัติ · ตรวจร่างกาย · Dx
        </button>
        <button type="button" className="hv4-grp-tab" onClick={() => onSelectMenu("gallery")}>
          Image Gallery
        </button>
        <span className="hv4-grp-tab hv4-grp-tab-on" aria-current="page">
          บันทึกการพยาบาล
        </span>
      </div>

      <div className="hv4-page-head">
        <h1 className="hv4-h1">บันทึกการพยาบาล</h1>
        <span className="hv4-page-sub">Nurse Note · Multidisciplinary · ของ Encounter นี้</span>
      </div>

      <section id="sec-nn" className="hv4-card">
        <h2 className="hv4-card-title">
          Nurse Note
          <SectionSpinner running={nurseSectionLoading} label="กำลังโหลด/บันทึกการพยาบาล" />
        </h2>

        {/* 86636 / 86635: live mode ใช้ dropdown "เลือก Focus" (ค่าจาก NurseNoteTemplate ของ division)
            ส่วน mock ยังใช้ dropdown Template ตามแบบ design bundle (PatientNursing.dc.html) */}
        {live ? (
          <div className="hv4-nn-focus-row" ref={nurseFormRef}>
            <div className="hv4-nn-focus-main">
              <label className="hv4-label" htmlFor="nn_focus">
                เลือก Focus
              </label>
              <SelectV4
                id="nn_focus"
                className="hv4-inline-select"
                placeholder={selectedFocus === null ? "เลือก Focus" : "เปลี่ยน Focus"}
                options={focusOptions}
                value={selectedFocus === null ? "" : String(selectedFocus.id)}
                onChange={(id) => chooseFocus(Number(id))}
              />
              {selectedFocus !== null && (
                <button
                  type="button"
                  className="hv4-focus-chip"
                  aria-label={`นำ Focus ${selectedFocus.name} ออกจากช่องบันทึก`}
                  onClick={clearFocus}
                >
                  ✖ {selectedFocus.name}
                </button>
              )}
              <div className="hv4-nn-focus-hint">
                เลือก Focus แล้วระบบจะเติมข้อความที่กรอกไว้ให้ในช่อง Assessment (A), Intervention (I)
                และ Evaluation (E) โดยอัตโนมัติ — เลือก Focus เดิมซ้ำได้อีก • ยกเลิกได้หากไม่ต้องการใช้ฟอร์มนี้
              </div>
              {focusOptions.length === 0 && (
                <div className="hv4-nn-focus-hint">ยังไม่มี Focus กดตั้งค่า template เพื่อสร้าง</div>
              )}
            </div>
            <ButtonV4 variant="ghost" aria-pressed={settingsOpen} onClick={openSettings}>
              ⚙ ตั้งค่า template
            </ButtonV4>
          </div>
        ) : (
        <div className="hv4-inline-row">
          <div>
            <label className="hv4-label" htmlFor="nn_tpl">
              Template
            </label>
            <SelectV4
              id="nn_tpl"
              className="hv4-inline-select"
              options={templates.map((item) => ({ value: item.name, text: item.name }))}
              value={template.name}
              onChange={(name) => {
                setTemplateName(name);
                setFieldValues(emptyFieldValues());
              }}
            />
          </div>
          <ButtonV4 variant="ghost" aria-pressed={settingsOpen} onClick={openSettings}>
            ⚙ ตั้งค่า template
          </ButtonV4>
        </div>
        )}

        {/* 86637: กล่องตั้งค่า template (live mode) — อ่าน/เขียนผ่าน API ของ PTM จริง (#86781) */}
        {settingsOpen && live && (
          <div className="hv4-tpl-settings">
            <b>ตั้งค่า template บันทึกการพยาบาล</b>
            <div className="hv4-modal-note">
              template คือข้อความสำเร็จรูปของ 3 ช่อง Assessment (A), Intervention (I), Evaluation (E) •
              แก้หรือลบ template ไม่กระทบ Nurse Note ที่บันทึกไปแล้ว
            </div>

            <div className="hv4-inline-row">
              <div>
                <label className="hv4-label" htmlFor="nn_tpl_sel">
                  เลือก Focus ที่ต้องการแก้ไข
                </label>
                <SelectV4
                  id="nn_tpl_sel"
                  className="hv4-inline-select"
                  options={[
                    { value: NURSE_FOCUS_NEW, text: NURSE_TEMPLATE_NEW_OPTION },
                    ...focusTemplates.map((item) => ({
                      value: String(item.id),
                      text: item.name,
                      preview: nurseFocusPreviewOf(item),
                    })),
                  ]}
                  value={templateDraft.itemId === null ? NURSE_FOCUS_NEW : String(templateDraft.itemId)}
                  onChange={pickFocusToEdit}
                />
              </div>
              <ButtonV4 variant="ghost" onClick={startNewFocusTemplate}>
                + สร้าง template ใหม่
              </ButtonV4>
            </div>

            <div className="hv4-tpl-form">
              <div>
                <label className="hv4-label" htmlFor="nn_tpl_focus">
                  Focus *
                </label>
                <input
                  id="nn_tpl_focus"
                  className="hv4-input"
                  value={templateDraft.name}
                  aria-required="true"
                  onChange={(event) => setTemplateDraft((current) => ({ ...current, name: event.target.value }))}
                />
              </div>
              {NURSE_AIE_FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="hv4-label" htmlFor={`nn_tpl_${field.key}`}>
                    {field.label}
                  </label>
                  <textarea
                    id={`nn_tpl_${field.key}`}
                    className="hv4-input hv4-textarea"
                    rows={3}
                    value={templateDraft.texts[field.key] ?? ""}
                    onChange={(event) => setDraftText(field.key, event.target.value)}
                  />
                </div>
              ))}
            </div>

            {templateHint !== "" && (
              <div className="hv4-modal-note" role="alert">
                {templateHint}
              </div>
            )}

            <div className="hv4-inline-row">
              <ButtonV4 variant="ok" onClick={saveFocusTemplate}>
                บันทึก template
              </ButtonV4>
              {templateDraft.itemId !== null && (
                <ButtonV4 variant="danger-ghost" onClick={deleteFocusTemplate}>
                  ลบ template นี้
                </ButtonV4>
              )}
              <ButtonV4 variant="ghost" onClick={() => setSettingsOpen(false)}>
                ปิด
              </ButtonV4>
            </div>
          </div>
        )}

        {/* mock mode: คงกล่องเดิมของ design bundle (PatientNursing.dc.html) เพื่อ visual gate */}
        {settingsOpen && !live && (
          <div className="hv4-tpl-settings">
            <b>ตั้งค่า template บันทึกการพยาบาล</b>
            <div>
              <label className="hv4-label" htmlFor="nn_sn">
                ชื่อ template
              </label>
              <input
                id="nn_sn"
                className="hv4-input"
                value={settingsName}
                onChange={(event) => setSettingsName(event.target.value)}
              />
            </div>
            <div>
              <label className="hv4-label" htmlFor="nn_sf">
                ช่องบันทึก (1 บรรทัด = 1 ช่อง)
              </label>
              <textarea
                id="nn_sf"
                className="hv4-input hv4-textarea"
                rows={4}
                value={settingsFields}
                onChange={(event) => setSettingsFields(event.target.value)}
              />
            </div>
            <div className="hv4-inline-row">
              <ButtonV4 variant="ok" onClick={saveTemplate}>
                บันทึก template
              </ButtonV4>
              <ButtonV4 variant="danger-ghost" onClick={deleteTemplate}>
                ลบ template นี้
              </ButtonV4>
              <ButtonV4 variant="ghost" onClick={() => setSettingsOpen(false)}>
                ปิด
              </ButtonV4>
            </div>
          </div>
        )}

        <div className="hv4-nn-fields">
          {activeFields.map((field) => (
            <div key={field.key}>
              <span className="hv4-label">{field.label}</span>
              <textarea
                className="hv4-input hv4-textarea"
                rows={live ? 3 : 2}
                aria-label={field.label}
                value={fieldValues[field.key] ?? ""}
                onChange={(event) => {
                  /* ต้อง capture ค่าก่อนเข้า updater: synthetic event ของ React 16 ถูก recycle
                     ถ้าอ่าน event.target.value ด้านใน updater จะได้ค่าว่าง (เจอบาง mode) */
                  const nextValue = event.target.value;
                  setFieldValues((current) => ({ ...current, [field.key]: nextValue }));
                }}
              />
            </div>
          ))}
        </div>

        <div className={live ? "hv4-nn-save-row" : "hv4-mt-8"}>
          {live && (doctorOrders ?? []).length > 0 && (
            <FieldV4 label="ผูกกับคำสั่งแพทย์ (doctor_order_id)" htmlFor="nn_order" className="hv4-md-cred">
              <SelectV4
                id="nn_order"
                options={[{ value: "0", text: "ไม่ผูกกับคำสั่ง" }].concat(
                  (doctorOrders ?? []).map((order) => ({
                    value: String(order.id),
                    text: order.label === "" ? `คำสั่ง ${order.id}` : order.label,
                  })),
                )}
                value={String(noteDoctorOrderId ?? 0)}
                onChange={(value) => setNoteDoctorOrderId(Number(value) || null)}
              />
            </FieldV4>
          )}
          {live && (
            <FieldV4 label="เวรที่บันทึก (working shift)" htmlFor="nn_shift" className="hv4-md-cred">
              <input
                id="nn_shift"
                className="hv4-input"
                placeholder="เช่น 8-16 น."
                aria-label="เวรที่บันทึก"
                value={noteShift}
                onChange={(event) => setNoteShift(event.target.value)}
              />
            </FieldV4>
          )}
          {live && (
            <FieldV4 label="Username (ผู้บันทึก)" htmlFor="nn_user" className="hv4-md-cred">
              <input
                id="nn_user"
                className="hv4-input"
                autoComplete="off"
                value={noteCredentials.username}
                onChange={(event) => setNoteCredential("username", event.target.value, "save")}
              />
            </FieldV4>
          )}
          {live && (
            <FieldV4 label="Password (ผู้บันทึก)" htmlFor="nn_pass" className="hv4-md-cred">
              <input
                id="nn_pass"
                type="password"
                className="hv4-input"
                autoComplete="off"
                value={noteCredentials.password}
                onChange={(event) => setNoteCredential("password", event.target.value, "save")}
              />
            </FieldV4>
          )}
          <ButtonV4
            variant="primary"
            disabled={
              addNoteDisabled || busy(ACTIONS.SAVE_NURSE_NOTE) || busy(ACTIONS.UPDATE_NURSE_NOTE)
            }
            onClick={addNote}
          >
            {editingNoteId === null ? "+ เพิ่มบันทึก" : "บันทึกการแก้ไข"}
          </ButtonV4>
          {editingNoteId !== null && (
            <ButtonV4 variant="ghost" onClick={cancelNoteEdit}>
              ยกเลิกการแก้ไข
            </ButtonV4>
          )}
        </div>

        {/* ข้อความ loading/error ของ nurse วางติดกับปุ่มเพิ่ม/บันทึกแก้ไข */}
        {live && busy(ACTIONS.LOAD_NURSE) && (
          <div role="status" className="hv4-note hv4-mt-8">
            กำลังโหลดบันทึกการพยาบาลของ Encounter นี้…
          </div>
        )}

        {live && loadError() !== null && (
          <AlertV4
            tone="danger"
            className="hv4-mt-8"
            onClose={() => setProp?.(`errorMessage.${cardName}`, null)}
          >
            อ่านบันทึกการพยาบาลไม่สำเร็จ: {loadError()}
          </AlertV4>
        )}

        {live && (
          <div className="hv4-note hv4-mt-8">
            backend ต้องยืนยันผู้บันทึกก่อนบันทึกการพยาบาล: ใส่ username/password หรือใช้ token ของ
            user ที่ login อยู่ (ถ้ามี ระบบจะส่ง user_token แทน)
          </div>
        )}

        {live ? (
          /* 86638 / 86641: ตารางรายการบันทึก + ค้นหาช่วงวันที่ + เลือกเพื่อ Print */
          <div className="hv4-nn-table-wrap">
            <div className="hv4-nn-toolbar">
              <div>
                <b>รายการบันทึก Nurse Note</b>
                <div className="hv4-nn-count">
                  เลือกแล้ว {checkedNoteIds.length}/{liveNoteRows.length} รายการสำหรับ Print
                </div>
              </div>
              <div className="hv4-nn-search">
                <FieldV4 label="ตั้งแต่วันที่" htmlFor="nn_from" className="hv4-nn-date">
                  <div className="hv4-date">
                    <DateTextBox
                      id="nn_from"
                      placeholder="DD/MM/YYYY"
                      value={noteSearchFrom}
                      onChange={(date: string) => setNoteSearchFrom(date)}
                    />
                  </div>
                </FieldV4>
                <FieldV4 label="ถึงวันที่" htmlFor="nn_to" className="hv4-nn-date">
                  <div className="hv4-date">
                    <DateTextBox
                      id="nn_to"
                      placeholder="DD/MM/YYYY"
                      value={noteSearchTo}
                      onChange={(date: string) => setNoteSearchTo(date)}
                    />
                  </div>
                </FieldV4>
                <ButtonV4 variant="primary" onClick={applyNoteSearch}>
                  ค้นหา
                </ButtonV4>
                <ButtonV4
                  variant="ghost"
                  disabled={checkedNoteIds.length === 0}
                  title={
                    checkedNoteIds.length === 0
                      ? "กรุณาเลือกอย่างน้อย 1 รายการก่อนพิมพ์"
                      : "พิมพ์รายการที่เลือก"
                  }
                  onClick={printSelectedNotes}
                >
                  🖨 Print ({checkedNoteIds.length})
                </ButtonV4>
              </div>
            </div>

            <div className="hv4-table-scroll">
              <table className="hv4-table hv4-nn-table">
                <thead>
                  <tr>
                    <th className="hv4-th-check">
                      <input
                        type="checkbox"
                        className="hv4-check-box"
                        aria-label="เลือกทุกรายการสำหรับ Print"
                        checked={
                          liveNoteRows.length > 0 && checkedNoteIds.length === liveNoteRows.length
                        }
                        onChange={toggleAllNotesChecked}
                      />
                    </th>
                    <th>วันที่และเวลาที่สร้าง</th>
                    <th>ผู้บันทึก</th>
                    <th>Focus</th>
                    <th>
                      Progress Note
                      <div className="hv4-th-sub">A: Assessment &middot; I: Intervention &middot; E: Evaluation</div>
                    </th>
                    <th className="hv4-th-action" />
                  </tr>
                </thead>
                <tbody>
                  {liveNoteRows.map((note) => {
                    const editing = editingNoteId === note.id;
                    const view = noteViewFromApi(note);
                    const parts = nurseNoteProgressParts(note);
                    return (
                      <React.Fragment key={note.id}>
                        <tr className={editing ? "hv4-row-editing" : undefined}>
                          <td>
                            <input
                              type="checkbox"
                              className="hv4-check-box"
                              aria-label={`เลือกบันทึกลำดับที่ ${note.id} สำหรับ Print`}
                              checked={checkedNoteIds.includes(note.id)}
                              onChange={() => toggleNoteChecked(note.id)}
                            />
                          </td>
                          <td>
                            <b>{note.date}</b>
                            {editing && <span className="hv4-edit-badge">กำลังแก้ไข</span>}
                            {editing && note.edited !== "" && (
                              <div className="hv4-note-meta">แก้ไขครั้งล่าสุด {note.edited}</div>
                            )}
                          </td>
                          <td>{note.nurse === "" ? "-" : note.nurse}</td>
                          <td>
                            <b className="hv4-nn-focus-cell">
                              {note.focus === "" ? "-" : note.focus}
                            </b>
                          </td>
                          <td className="hv4-nn-progress">
                            {parts.length === 0 ? (
                              "-"
                            ) : (
                              parts.map((part) => (
                                <div key={part.short} className="hv4-nn-part">
                                  <b>{part.short}:</b> {part.text}
                                </div>
                              ))
                            )}
                          </td>
                          <td className="hv4-td-action">
                            <ButtonV4
                              className="hv4-btn-icon hv4-btn-icon-warn"
                              aria-label="แก้ไขบันทึกการพยาบาล"
                              title="แก้ไข"
                              onClick={() => openNoteEdit(view)}
                            >
                              &#9998;
                            </ButtonV4>
                            <ButtonV4
                              className="hv4-btn-icon hv4-btn-icon-danger"
                              aria-label="ลบบันทึกการพยาบาล"
                              title="ลบ"
                              onClick={() => startNoteDelete(view)}
                            >
                              &#128465;
                            </ButtonV4>
                          </td>
                        </tr>
                        {/* 86640: แถบยืนยันลบใต้แถว (no modal) กรอกได้เฉพาะรหัสผ่านของผู้ใช้ที่ login */}
                        {deletingNote !== null && deletingNote.id === note.id && (
                          <tr className="hv4-del-strip">
                            <td colSpan={6}>
                              <span>
                                กรุณากรอกรหัสผ่านของผู้ใช้เพื่อยืนยันการลบรายการนี้ &mdash; ลบได้เฉพาะ
                                บันทึกของตัวท่านเอง
                              </span>
                              <input
                                id="nn_del_pass"
                                type="password"
                                className="hv4-input hv4-del-pass"
                                autoComplete="off"
                                aria-label="รหัสผ่านของผู้บันทึก"
                                placeholder="Password"
                                value={noteDeleteCredentials.password}
                                onChange={(event) =>
                                  setNoteCredential("password", event.target.value, "delete")
                                }
                              />
                              <ButtonV4
                                variant="confirm"
                                disabled={
                                  noteDeleteCredentials.password.trim() === "" ||
                                  busy(ACTIONS.DELETE_NURSE_NOTE)
                                }
                                onClick={confirmDeleteNote}
                              >
                                ลบ
                              </ButtonV4>
                              <ButtonV4 variant="ghost" onClick={cancelNoteDelete}>
                                ยกเลิก
                              </ButtonV4>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {liveNoteRows.length === 0 && (
              <div className="hv4-empty">
                {noteSearchApplied === null
                  ? "ยังไม่มีบันทึกการพยาบาลใน Encounter นี้"
                  : "ไม่พบบันทึกในช่วงวันที่ที่ค้นหา"}
              </div>
            )}

            {/* สรุปผลบันทึก/แก้ไข/ลบ (86635 ข้อ 5, 86639 ข้อ 4, 86640 ข้อ 5) */}
            {examSlice !== null &&
              examSlice.nurseSavedMessage !== "" &&
              dismissedNurseMessage !== examSlice.nurseSavedMessage && (
              <div className="hv4-save-msg" role="status">
                {examSlice.nurseSavedMessage}
                <button
                  type="button"
                  className="hv4-save-msg-close"
                  aria-label="ปิดข้อความยืนยัน"
                  onClick={() => setDismissedNurseMessage(examSlice.nurseSavedMessage)}
                >
                  &#10005;
                </button>
              </div>
            )}
            {notePrintMessage !== null && (
              <div className="hv4-save-msg" role="status">
                {notePrintMessage}
                <button
                  type="button"
                  className="hv4-save-msg-close"
                  aria-label="ปิดข้อความยืนยันการพิมพ์"
                  onClick={() => setNotePrintMessage(null)}
                >
                  &#10005;
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="hv4-note-list">
            {notes
              .slice()
              .reverse()
              .map((note, index) => (
                <div key={`${note.at}-${index}`} className="hv4-note-item">
                  <div>
                    {note.template !== "" && <b>{note.template}</b>}
                    {note.text}
                  </div>
                  <div className="hv4-note-meta">
                    {note.by} &middot; {note.at}
                    {note.shift ? ` &middot; เวร ${note.shift}` : ""}
                    {note.orderId ? ` &middot; order ${note.orderId}` : ""}
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>

      <section id="sec-md" className="hv4-card">
        {/* 86643 ข้อ 1: มีปุ่มย่อ/ขยายที่หัวข้อ card (live mode) */}
        <div className="hv4-card-head">
          <h2 className="hv4-card-title">
            Multidisciplinary
            <SectionSpinner running={consultSectionLoading} label="กำลังโหลด/บันทึกทีมสหสาขา" />
          </h2>
          {live && (
            <ButtonV4
              variant="ghost"
              aria-expanded={consultOpen}
              aria-label={consultOpen ? "ย่อ Multidisciplinary" : "ขยาย Multidisciplinary"}
              title={consultOpen ? "ย่อ Multidisciplinary" : "ขยาย Multidisciplinary"}
              onClick={() => setConsultOpen((current) => !current)}
            >
              {consultOpen ? "▼" : "▶"}
            </ButtonV4>
          )}
        </div>

        {consultOpen && (
          <>
        <div className="hv4-md-grid" ref={consultFormRef}>
          {!live && (
          <FieldV4 label="วิชาชีพ" htmlFor="md_d">
            <SelectV4
              id="md_d"
              options={(live ? consultRoleLabels : [...NURSE_DISCIPLINES]).map((item) => ({
                value: item,
                text: item,
              }))}
              value={live ? consultRoleLabel : discipline}
              onChange={(label) => {
                if (live) {
                  handleConsultDiscipline(label);
                } else {
                  setDiscipline(label);
                }
              }}
            />
          </FieldV4>
          )}

          <FieldV4
            label={
              live
                ? editingConsult === null
                  ? "Problem *"
                  : "Problem * (กำลังแก้ไขรายการที่เลือก)"
                : editingConsult === null
                ? "บันทึก"
                : "บันทึก (กำลังแก้ไข)"
            }
            htmlFor="md_t"
            className="hv4-md-wide"
          >
            <textarea
              id="md_t"
              className="hv4-input hv4-textarea"
              rows={live ? 3 : 2}
              value={live ? consultDraft.problem : disciplineText}
              onChange={(event) => {
                const value = event.target.value;
                if (live) {
                  setConsultDraftField("problem", value);
                } else {
                  setDisciplineText(value);
                }
              }}
            />
            {live && (
              <span className="hv4-note">Problem (จำเป็นตาม HIS V3 MultiDisciplinaryEditor)</span>
            )}
          </FieldV4>

          {live && (
            <>
              <FieldV4 label="Start Date *" htmlFor="md_date" className="hv4-md-date-field">
                <div className="hv4-date">
                  <DateTextBox
                    id="md_date"
                    placeholder="DD/MM/YYYY"
                    value={consultDraft.date}
                    onChange={(date: string) => setConsultDraftField("date", date)}
                  />
                </div>
              </FieldV4>
              <FieldV4 label="End Date" htmlFor="md_end" className="hv4-md-date-field">
                <div className="hv4-date">
                  <DateTextBox
                    id="md_end"
                    placeholder="DD/MM/YYYY"
                    value={consultDraft.endDate}
                    onChange={(date: string) => setConsultDraftField("endDate", date)}
                  />
                </div>
              </FieldV4>
              <FieldV4 label="Plan" htmlFor="md_plan" className="hv4-md-wide">
                <textarea
                  id="md_plan"
                  className="hv4-input hv4-textarea"
                  rows={3}
                  value={consultDraft.plan}
                  onChange={(event) => setConsultDraftField("plan", event.target.value)}
                />
              </FieldV4>
              <FieldV4 label="Goal" htmlFor="md_goal" className="hv4-md-wide">
                <textarea
                  id="md_goal"
                  className="hv4-input hv4-textarea"
                  rows={3}
                  value={consultDraft.goal}
                  onChange={(event) => setConsultDraftField("goal", event.target.value)}
                />
              </FieldV4>
            </>
          )}
        </div>

        {live && (
          /* 86643 ข้อ 2: แถบ filter อยู่เหนือตาราง — Practitioner + ระบุวันที่ + ค้นหา */
          <div className="hv4-md-filter">
            <FieldV4 label="Practitioner" htmlFor="md_filter" className="hv4-md-date-field">
              <SelectV4
                id="md_filter"
                options={consultRoleLabels.map((item) => ({ value: item, text: item }))}
                value={consultRoleLabel}
                onChange={(label) => handleConsultDiscipline(label)}
              />
            </FieldV4>
            <div className="hv4-md-dates">
              <label className="hv4-check-label hv4-md-dates-check">
                <input
                  type="checkbox"
                  className="hv4-check-box"
                  aria-label="ระบุวันที่"
                  checked={consultUseDates}
                  onChange={() => handleConsultToggleDates()}
                />
                ระบุวันที่
              </label>
              <FieldV4 label="ตั้งแต่วันที่" htmlFor="md_from" className="hv4-md-date-field">
                <div className={consultUseDates ? "hv4-date" : "hv4-date hv4-date-off"}>
                  <DateTextBox
                    id="md_from"
                    placeholder="DD/MM/YYYY"
                    value={consultDateFrom}
                    onChange={(date: string) => {
                      /* ช่องถูก disable เมื่อไม่ได้ติ๊ก "ระบุวันที่" (86643 ข้อ 2) */
                      if (!consultUseDates) return;
                      handleConsultDateFrom(date);
                    }}
                  />
                </div>
              </FieldV4>
              <FieldV4 label="ถึงวันที่" htmlFor="md_to" className="hv4-md-date-field">
                <div className={consultUseDates ? "hv4-date" : "hv4-date hv4-date-off"}>
                  <DateTextBox
                    id="md_to"
                    placeholder="DD/MM/YYYY"
                    value={consultDateTo}
                    onChange={(date: string) => {
                      if (!consultUseDates) return;
                      handleConsultDateTo(date);
                    }}
                  />
                </div>
              </FieldV4>
              <ButtonV4 variant="primary" onClick={() => reloadConsult()}>
                ค้นหา
              </ButtonV4>
            </div>
          </div>
        )}

        {/* backend ยืนยันตัวผู้บันทึก (เหมือน HIS V3) -> Username/Password อยู่แถวเดียวกับปุ่มบันทึก */}
        <div className={live ? "hv4-md-save-row" : "hv4-mt-8"}>
          {live && (
            <FieldV4 label="Username (ผู้บันทึก) *" htmlFor="md_user" className="hv4-md-cred">
              {" "}
              <input
                id="md_user"
                className="hv4-input"
                autoComplete="off"
                value={consultDraft.username}
                onChange={(event) => setConsultDraftField("username", event.target.value)}
              />
            </FieldV4>
          )}
          {live && (
            <FieldV4 label="Password (ผู้บันทึก) *" htmlFor="md_pass" className="hv4-md-cred">
              <input
                id="md_pass"
                type="password"
                className="hv4-input"
                autoComplete="off"
                value={consultDraft.password}
                onChange={(event) => setConsultDraftField("password", event.target.value)}
              />
            </FieldV4>
          )}
          <ButtonV4
            variant="primary"
            disabled={
              live
                ? consultDraft.date.trim() === "" ||
                  consultDraft.problem.trim() === "" ||
                  consultDraft.username.trim() === "" ||
                  consultDraft.password === "" ||
                  consultSaving
                : addDisciplineDisabled
            }
            onClick={() => {
              if (live) saveConsult();
              else addDisciplineNote();
            }}
          >
            {live
              ? editingConsult === null
                ? "+ บันทึกการเพิ่ม"
                : "บันทึกการแก้ไข"
              : "+ เพิ่มบันทึกทีม"}
          </ButtonV4>
          {live && editingConsult !== null && (
            <ButtonV4 variant="ghost" onClick={cancelConsultEdit}>
              ยกเลิกการแก้ไข
            </ButtonV4>
          )}
          {live && consultSaving && <span className="hv4-note">กำลังบันทึก…</span>}
          {live && (
            <div className="hv4-nn-focus-hint">
              ใช้ Username และ Password ของผู้ใช้ที่ Login เพื่อระบุผู้บันทึก
            </div>
          )}
        </div>

        {/* แถบ error วางติดกับปุ่มของบันทึกทีมสหสาขา */}
        {live && consultError !== null && (
          <AlertV4
            tone="danger"
            onClose={() => setProp?.(`errorMessage.${CONSULT_CARD_NAME}`, null)}
          >
            อ่านบันทึกทีมสหสาขาไม่สำเร็จ: {consultError}
          </AlertV4>
        )}

        {live ? (
          /* 86643 / 86647: ตาราง Multidisciplinary + เลือกหลายรายการเพื่อพิมพ์ */
          <div className="hv4-nn-table-wrap">
            <div className="hv4-nn-toolbar">
              <div>
                <b>รายการ Multidisciplinary</b>
                <div className="hv4-nn-count">
                  เลือกแล้ว {checkedConsultIds.length}/{consultRows().length} รายการสำหรับพิมพ์
                </div>
              </div>
              <ButtonV4
                variant="ghost"
                disabled={checkedConsultIds.length === 0}
                title={
                  checkedConsultIds.length === 0
                    ? "กรุณาเลือกอย่างน้อย 1 รายการก่อนพิมพ์"
                    : "พิมพ์รายการที่เลือก"
                }
                onClick={printSelectedConsults}
              >
                🖨 พิมพ์ ({checkedConsultIds.length})
              </ButtonV4>
            </div>

            <div className="hv4-table-scroll">
              <table className="hv4-table hv4-md-table">
                <thead>
                  <tr>
                    <th className="hv4-th-check">
                      <input
                        type="checkbox"
                        className="hv4-check-box"
                        aria-label="เลือกรายการทั้งหมดสำหรับพิมพ์"
                        checked={
                          consultRows().length > 0 && checkedConsultIds.length === consultRows().length
                        }
                        onChange={toggleAllConsultChecked}
                      />
                    </th>
                    <th>Start Date</th>
                    <th>Problem</th>
                    <th>Plan</th>
                    <th>Goal</th>
                    <th>Practitioner</th>
                    <th>End Date</th>
                    <th className="hv4-th-action" />
                  </tr>
                </thead>
                <tbody>
                  {consultRows().map((note) => (
                    <tr
                      key={note.id}
                      className={editingConsultId === note.id ? "hv4-row-editing" : undefined}
                    >
                      <td>
                        <input
                          type="checkbox"
                          className="hv4-check-box"
                          aria-label={`เลือกรายการ ${note.id} สำหรับพิมพ์`}
                          checked={checkedConsultIds.includes(note.id)}
                          onChange={() => toggleConsultChecked(note.id)}
                        />
                      </td>
                      <td>
                        <b>{note.date === "" ? "-" : note.date}</b>
                        {editingConsultId === note.id && (
                          <span className="hv4-edit-badge">กำลังแก้ไขรายการที่เลือก</span>
                        )}
                      </td>
                      <td>{note.problem === "" ? "-" : note.problem}</td>
                      <td>{note.plan === "" ? "-" : note.plan}</td>
                      <td>{note.goal === "" ? "-" : note.goal}</td>
                      <td>
                        {note.practitionerName === "" ? "-" : note.practitionerName}
                        <div className="hv4-subtle">{note.discipline}</div>
                      </td>
                      <td>{note.endDate === "" ? "-" : note.endDate}</td>
                      <td className="hv4-td-action">
                        {/* 86644: ปุ่มดินสอ (เหลือง) เติมข้อมูลกลับเข้าฟอร์ม / แก้ได้เฉพาะรายการของตัวเอง */}
                        <ButtonV4
                          className="hv4-btn-icon hv4-btn-icon-warn"
                          aria-label="แก้ไขบันทึกทีมสหสาขา"
                          title="แก้ไข"
                          disabled={!consultNoteEditable(note, currentUserId)}
                          onClick={() => openConsultEdit(note)}
                        >
                          &#9998;
                        </ButtonV4>
                        {/* 86645: ไอคอนนาฬิกา (เทา) เปิด popup log */}
                        <ButtonV4
                          className="hv4-btn-icon hv4-btn-icon-muted"
                          aria-label="เปิดประวัติการบันทึก"
                          title="ประวัติ"
                          onClick={() => openConsultLogs(note)}
                        >
                          &#128337;
                        </ButtonV4>
                        {/* HIS V3 ลบบันทึกทีมได้ (CardMultiDisciplinaryView.tsx:625-632) — คงไว้แม้ issue
                            ไม่ได้ระบุปุ่มในตาราง เพื่อไม่ให้ความสามารถเดิมหายไป */}
                        <ButtonV4
                          className="hv4-btn-icon hv4-btn-icon-danger"
                          aria-label="ลบบันทึกทีมสหสาขา"
                          title="ลบ"
                          disabled={busyConsult(CONSULT_ACTIONS.DELETE)}
                          onClick={() => setDeletingConsultId(note.id)}
                        >
                          &#128465;
                        </ButtonV4>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {consultRows().length === 0 && (
              <div className="hv4-empty">
                {consultFilterRole === "" && !consultUseDates
                  ? "ยังไม่มี Multidisciplinary"
                  : "ไม่พบรายการตามเงื่อนไขที่ค้นหา"}
              </div>
            )}

            {consultSlice !== null &&
              consultSlice.savedMessage !== "" &&
              dismissedConsultMessage !== consultSlice.savedMessage && (
              <div className="hv4-save-msg" role="status">
                {consultSlice.savedMessage}
                <button
                  type="button"
                  className="hv4-save-msg-close"
                  aria-label="ปิดข้อความยืนยัน"
                  onClick={() => setDismissedConsultMessage(consultSlice.savedMessage)}
                >
                  &#10005;
                </button>
              </div>
            )}
            {consultPrintMessage !== null && (
              <div className="hv4-save-msg" role="status">
                {consultPrintMessage}
                <button
                  type="button"
                  className="hv4-save-msg-close"
                  aria-label="ปิดข้อความยืนยันการพิมพ์"
                  onClick={() => setConsultPrintMessage(null)}
                >
                  &#10005;
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="hv4-note-list">
            {disciplineNotes
              .slice()
              .reverse()
              .map((note, index) => (
                <div key={`${note.at}-${index}`} className="hv4-note-item">
                  <span className="hv4-disc-chip">{note.discipline}</span>
                  <div className="hv4-note-text">{note.text}</div>
                  <div className="hv4-note-meta">
                    {note.by} &middot; {note.at}
                  </div>
                </div>
              ))}
          </div>
        )}

        {live && busyConsult(CONSULT_ACTIONS.LOAD) && (
          <div role="status" className="hv4-note hv4-mt-8">
            กำลังโหลดบันทึกทีมสหสาขา…
          </div>
        )}

          </>
        )}

        {live && deletingConsult !== null && (
          <ModalV4
            title="ยืนยันการลบบันทึกทีมสหสาขา"
            width={620}
            onClose={() => setDeletingConsultId(null)}
          >
            <div className="hv4-note-item">
              <span className="hv4-disc-chip">{deletingConsult.discipline}</span>
              <div className="hv4-note-text">{consultNoteText(deletingConsult)}</div>
              <div className="hv4-note-meta">
                {consultAuthorText(deletingConsult)} · {consultDateRangeText(deletingConsult)}
              </div>
            </div>
            <div className="hv4-note hv4-mt-8">
              การลบเป็นการลบจริง (DELETE /apis/PTM/multidisciplinary-item/{"{"}pk{"}"}) และ backend
              ต้องยืนยันตัวผู้ลบด้วย username/password เหมือน HIS V3
            </div>
            <div className="hv4-md-cred-row">
              <FieldV4 label="Username *" htmlFor="md_del_user" className="hv4-md-cred">
                <input
                  id="md_del_user"
                  className="hv4-input"
                  autoComplete="off"
                  value={deleteCredentials.username}
                  onChange={(event) =>
                    setDeleteCredentials((current) => ({
                      ...current,
                      username: event.target.value,
                    }))
                  }
                />
              </FieldV4>
              <FieldV4 label="Password *" htmlFor="md_del_pass" className="hv4-md-cred">
                <input
                  id="md_del_pass"
                  type="password"
                  className="hv4-input"
                  autoComplete="off"
                  value={deleteCredentials.password}
                  onChange={(event) =>
                    setDeleteCredentials((current) => ({
                      ...current,
                      password: event.target.value,
                    }))
                  }
                />
              </FieldV4>
            </div>
            <div className="hv4-inline-row hv4-mt-8">
              <ButtonV4 variant="confirm" onClick={confirmDeleteConsult}>
                ลบบันทึก
              </ButtonV4>
              <ButtonV4 variant="ghost" onClick={() => setDeletingConsultId(null)}>
                ยกเลิก
              </ButtonV4>
            </div>
          </ModalV4>
        )}

        {live && logsConsultId !== null && (
          <ModalV4
            title="ประวัติ"
            width={760}
            onClose={() => setLogsConsultId(null)}
          >
            {/* 86645 ข้อ 1: หัว popup แสดง "ประวัติ" + Problem ของรายการที่กำลังดู */}
            <div className="hv4-log-problem">
              {editingConsultLogs === null ? "-" : editingConsultLogs.problem === "" ? "-" : editingConsultLogs.problem}
            </div>

            <table className="hv4-table hv4-log-table">
              <thead>
                <tr>
                  <th>Date-time</th>
                  <th>Edit user</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {consultLogs.map((log, index) => {
                  const user = consultLogUserParts(log.user);
                  return (
                    <tr key={`${log.id ?? index}`}>
                      <td>{log.datetime}</td>
                      <td>
                        {user.name}
                        {user.code !== "" && <div className="hv4-subtle">{user.code}</div>}
                      </td>
                      <td>
                        <span className={consultLogBadgeClassOf(log.action)}>{log.action}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {consultLogs.length === 0 && (
              <div className="hv4-empty">ยังไม่มีประวัติการบันทึกของรายการนี้</div>
            )}
          </ModalV4>
        )}
      </section>
    </>
  );
};

export default CardNursingV4;
